import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import GroupIcon from "@material-ui/icons/Group";
import ViewAgendaIcon from "@material-ui/icons/ViewAgenda";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";

import { openApi } from "../../services/api";

const AVATAR_COLORS = [
  "#2563eb", "#ea580c", "#16a34a",
  "#7c3aed", "#db2777", "#0891b2", "#ca8a04",
];

const darken = (hex, amount = 0.25) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  let r = Math.round(((num >> 16) & 255) * (1 - amount));
  let g = Math.round(((num >> 8) & 255) * (1 - amount));
  let b = Math.round((num & 255) * (1 - amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const readableOn = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const toLin = (v) => {
    const c = parseInt(v, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L =
    0.2126 * toLin(h.slice(0, 2)) +
    0.7152 * toLin(h.slice(2, 4)) +
    0.0722 * toLin(h.slice(4, 6));
  return L > 0.55 ? "#1f2937" : "#fff";
};

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const formatPrice = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatLongDate = (ymd) => {
  const d = new Date(`${ymd}T00:00:00`);
  const s = d.toLocaleDateString("pt-BR", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: theme.palette.type === "dark" ? "#1c1c1c" : "#f0f2f5",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    borderRadius: 16,
    overflow: "hidden",
    padding: theme.spacing(3),
  },
  // ── Hero header (bleed to card edges via negative margins) ─────────────────
  heroHeader: {
    margin: theme.spacing(-3, -3, 2.5, -3),
    padding: theme.spacing(2.5, 3),
    borderRadius: "16px 16px 0 0",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: theme.spacing(0.4, 1.25),
    borderRadius: 20,
    background: "rgba(255,255,255,0.18)",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.4px",
    marginBottom: theme.spacing(1.5),
    cursor: "default",
    userSelect: "none",
  },
  brandHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  brandAvatar: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: 700,
    flexShrink: 0,
    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  brandLogoImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    padding: 6,
  },
  brandName: {
    fontWeight: 800,
    lineHeight: 1.05,
    letterSpacing: "-0.5px",
  },
  // ── Filter card ────────────────────────────────────────────────────────────
  filterCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: "4px solid transparent",
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    background: theme.palette.type === "dark" ? "#262626" : "#f8fafc",
  },
  filterCardLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  section: {
    marginTop: theme.spacing(2),
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    textAlign: "center",
  },
  successIcon: {
    fontSize: 64,
    color: "#4caf50",
    marginBottom: theme.spacing(1),
  },
  // ── Slots section header ───────────────────────────────────────────────────
  slotsHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  slotsHeaderIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  slotsTitle: {
    fontWeight: 700,
    lineHeight: 1.1,
  },
  freeCount: {
    fontWeight: 600,
  },
  // ── Provider filter pills ──────────────────────────────────────────────────
  pillsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  pill: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 12,
    border: `1.5px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    transition: "all .15s",
  },
  pillAvatar: {
    width: 26,
    height: 26,
    fontSize: "0.7rem",
    fontWeight: 700,
  },
  pillCount: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.72rem",
    fontWeight: 700,
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  pillCountOnPrimary: {
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
  },
  // ── Layout toggle ──────────────────────────────────────────────────────────
  layoutToggleRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1.5),
  },
  layoutToggle: {
    display: "inline-flex",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  layoutBtn: {
    border: "none",
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75, 1.5),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  layoutBtnActive: {
    background: theme.palette.type === "dark" ? "#1e293b" : "#0f172a",
    color: "#fff",
  },
  // ── Provider cards ─────────────────────────────────────────────────────────
  columnsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "stretch",
  },
  providerCard: {
    position: "relative",
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.type === "dark" ? "#262626" : "#f8fafc",
    borderLeft: "4px solid transparent",
  },
  providerColumn: {
    flex: "1 1 280px",
    minWidth: 0,
    marginBottom: 0,
  },
  legendRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  legendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 4,
    flexShrink: 0,
  },
  providerHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  providerAvatar: {
    width: 44,
    height: 44,
    fontWeight: 700,
  },
  providerName: {
    fontWeight: 700,
    lineHeight: 1.1,
  },
  durationChip: {
    marginLeft: "auto",
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 20,
    padding: theme.spacing(0.5, 1.25),
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
  },
  // ── Slot buttons ───────────────────────────────────────────────────────────
  slotChipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  slotItem: {
    position: "relative",
  },
  slotBtn: {
    minWidth: 106,
    borderRadius: 10,
    border: `1.5px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    padding: theme.spacing(1.25, 1.5),
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    cursor: "pointer",
    transition: "all .12s",
  },
  nearestBadge: {
    position: "absolute",
    top: -9,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "0.6rem",
    fontWeight: 700,
    borderRadius: 6,
    padding: "1px 6px",
    whiteSpace: "nowrap",
    zIndex: 1,
  },
  // ── Confirm section ────────────────────────────────────────────────────────
  confirmSection: {
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: "1px solid transparent",
  },
  confirmHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    fontWeight: 700,
    fontSize: "0.9rem",
  },
}));

const PublicBooking = () => {
  const classes = useStyles();
  const { slug } = useParams();

  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [business, setBusiness] = useState(null);

  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [providerFilter, setProviderFilter] = useState("all");
  const [layout, setLayout] = useState("grouped");

  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const selectedService = business?.services?.find((s) => s.id === serviceId);

  const brandColors =
    business?.company?.brandColors && business.company.brandColors.length
      ? business.company.brandColors
      : [];
  const primary = brandColors[0] || "#2563eb";
  const gradientEnd = brandColors[1] || darken(primary, 0.3);
  const brandGradient = `linear-gradient(135deg, ${primary}, ${gradientEnd})`;
  const providerPalette = brandColors.length ? brandColors : AVATAR_COLORS;
  const providerColor = (id) =>
    providerPalette[Math.abs(Number(id) || 0) % providerPalette.length];

  const onPrimary = readableOn(primary);
  const primaryOnLight = onPrimary === "#fff" ? primary : darken(primary, 0.4);

  const logoUrl = business?.company?.logo
    ? `${process.env.REACT_APP_BACKEND_URL}/public/${business.company.logo}`
    : null;

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const { data } = await openApi.get(`/public/booking/${slug}`);
        setBusiness(data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoadingBusiness(false);
      }
    };
    fetchBusiness();
  }, [slug]);

  const fetchSlots = useCallback(async () => {
    if (!serviceId || !date) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const { data } = await openApi.get(`/public/booking/${slug}/slots`, {
        params: { serviceId, date },
      });
      setSlots(data.slots || []);
    } catch (err) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [slug, serviceId, date]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const groups = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      if (!map.has(slot.userId)) {
        map.set(slot.userId, { userId: slot.userId, userName: slot.userName, slots: [] });
      }
      map.get(slot.userId).slots.push(slot);
    });
    return Array.from(map.values());
  }, [slots]);

  const earliestStartAt = useMemo(() => {
    if (slots.length === 0) return null;
    return slots.reduce((min, s) => (s.startAt < min ? s.startAt : min), slots[0].startAt);
  }, [slots]);

  const visibleGroups = useMemo(() => {
    if (providerFilter === "all") return groups;
    return groups.filter((g) => String(g.userId) === String(providerFilter));
  }, [groups, providerFilter]);

  const handleSubmit = async () => {
    if (!selectedSlot || !contactName || !contactNumber) {
      toast.error("Preencha seu nome, WhatsApp e escolha um horário.");
      return;
    }
    setSubmitting(true);
    try {
      await openApi.post(`/public/booking/${slug}`, {
        serviceId,
        userId: selectedSlot.userId,
        startAt: selectedSlot.startAt,
        contactName,
        contactNumber,
      });
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.error === "ERR_SLOT_NOT_AVAILABLE"
          ? "Este horário acabou de ser preenchido. Escolha outro, por favor."
          : "Não foi possível concluir o agendamento. Tente novamente.";
      toast.error(msg);
      fetchSlots();
    } finally {
      setSubmitting(false);
    }
  };

  const renderSlot = (slot) => {
    const isSelected =
      selectedSlot &&
      selectedSlot.startAt === slot.startAt &&
      selectedSlot.userId === slot.userId;
    const isNearest = slot.startAt === earliestStartAt;
    const slotStyle = isSelected
      ? { backgroundColor: primary, borderColor: primary, color: onPrimary }
      : isNearest
      ? { borderColor: primary, background: `${primary}18` }
      : undefined;

    return (
      <div className={classes.slotItem} key={`${slot.startAt}-${slot.userId}`}>
        {isNearest && (
          <span
            className={classes.nearestBadge}
            style={{ background: primary, color: onPrimary }}
          >
            Mais próximo
          </span>
        )}
        <button
          type="button"
          className={classes.slotBtn}
          style={slotStyle}
          onClick={() => setSelectedSlot(slot)}
        >
          {slot.time}
        </button>
      </div>
    );
  };

  const renderProviderCard = (group, asColumn) => (
    <Paper
      key={group.userId}
      elevation={0}
      className={`${classes.providerCard} ${asColumn ? classes.providerColumn : ""}`}
      style={{ borderLeftColor: providerColor(group.userId) }}
    >
      <Box className={classes.providerHeader}>
        <Avatar
          className={classes.providerAvatar}
          style={{
            backgroundColor: providerColor(group.userId),
            color: readableOn(providerColor(group.userId)),
          }}
        >
          {initials(group.userName)}
        </Avatar>
        <Box>
          <Typography className={classes.providerName}>{group.userName}</Typography>
          <Typography variant="caption" color="textSecondary">
            {`${group.slots.length} ${
              group.slots.length === 1 ? "horário disponível" : "horários disponíveis"
            }`}
          </Typography>
        </Box>
        {selectedService && (
          <span className={classes.durationChip}>{`${selectedService.durationMinutes} min`}</span>
        )}
      </Box>
      <div className={classes.slotChipsWrap}>{group.slots.map(renderSlot)}</div>
    </Paper>
  );

  if (loadingBusiness) {
    return (
      <div className={classes.root}>
        <Container maxWidth="md">
          <div className={classes.center}>
            <CircularProgress />
          </div>
        </Container>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={classes.root}>
        <Container maxWidth="sm">
          <Paper className={classes.paper}>
            <div className={classes.center}>
              <Typography variant="h6">Página não encontrada</Typography>
              <Typography variant="body2" color="textSecondary">
                Verifique o link de agendamento.
              </Typography>
            </div>
          </Paper>
        </Container>
      </div>
    );
  }

  if (done) {
    return (
      <div className={classes.root}>
        <Container maxWidth="sm">
          <Paper className={classes.paper} style={{ padding: 0 }}>
            {/* mini hero on success */}
            <Box
              className={classes.heroHeader}
              style={{ background: brandGradient, margin: 0, borderRadius: 16 }}
            >
              <Box className={classes.heroBadge} style={{ color: onPrimary }}>
                <EventAvailableIcon style={{ fontSize: 13 }} />
                Agendamento Online
              </Box>
              <Box className={classes.brandHeader}>
                <div className={classes.brandAvatar}>
                  {logoUrl ? (
                    <img src={logoUrl} alt={business.company.name} className={classes.brandLogoImg} />
                  ) : (
                    <span style={{ color: primary }}>{initials(business.company.name)}</span>
                  )}
                </div>
                <Box>
                  <Typography variant="h5" className={classes.brandName} style={{ color: onPrimary }}>
                    {business.company.name}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <div className={classes.center}>
              <CheckCircleIcon className={classes.successIcon} />
              <Typography variant="h6" gutterBottom>
                Agendamento confirmado!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${selectedService?.name} em ${moment(selectedSlot.startAt).format(
                  "DD/MM/YYYY [às] HH:mm"
                )}`}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Você receberá a confirmação no WhatsApp. Até logo!
              </Typography>
            </div>
          </Paper>
        </Container>
      </div>
    );
  }

  const multipleProviders = groups.length > 1;

  return (
    <div className={classes.root}>
      <Container maxWidth="md">
        <Paper className={classes.paper} elevation={1}>

          {/* ── Hero header ─────────────────────────────────────────────── */}
          <Box className={classes.heroHeader} style={{ background: brandGradient }}>
            <Box className={classes.heroBadge} style={{ color: onPrimary }}>
              <EventAvailableIcon style={{ fontSize: 13 }} />
              Agendamento Online
            </Box>
            <Box className={classes.brandHeader}>
              <div className={classes.brandAvatar}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={business.company.name}
                    className={classes.brandLogoImg}
                  />
                ) : (
                  <span style={{ color: primary }}>{initials(business.company.name)}</span>
                )}
              </div>
              <Box>
                <Typography
                  variant="h4"
                  className={classes.brandName}
                  style={{ color: onPrimary }}
                >
                  {business.company.name}
                </Typography>
                <Typography variant="body2" style={{ color: onPrimary, opacity: 0.82 }}>
                  Agende seu horário online
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Main content ─────────────────────────────────────────────── */}
          {business.services.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum serviço disponível para agendamento no momento.
            </Typography>
          ) : (
            <>
              {/* Filter card */}
              <Box
                className={classes.filterCard}
                style={{ borderLeftColor: primary }}
              >
                <Typography
                  variant="caption"
                  className={classes.filterCardLabel}
                  style={{ color: primaryOnLight }}
                >
                  <EventAvailableIcon fontSize="small" />
                  Escolha o serviço e a data
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    style={{ flex: "1 1 260px", marginTop: 0 }}
                  >
                    <InputLabel>Serviço</InputLabel>
                    <Select
                      value={serviceId}
                      onChange={(e) => {
                        setServiceId(e.target.value);
                        setProviderFilter("all");
                        setSelectedSlot(null);
                      }}
                      label="Serviço"
                      renderValue={(value) => {
                        const s = business.services.find((svc) => svc.id === value);
                        if (!s) return "";
                        return `${s.name} • ${s.durationMinutes} min • ${formatPrice(s.price)}`;
                      }}
                    >
                      {business.services.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {s.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {`${s.durationMinutes} min • ${formatPrice(s.price)}`}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedService && (
                    <TextField
                      label="Data"
                      type="date"
                      value={date}
                      onChange={(e) => {
                        setDate(e.target.value);
                        setProviderFilter("all");
                        setSelectedSlot(null);
                      }}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      margin="dense"
                      style={{ flex: "0 1 190px", marginTop: 0 }}
                      inputProps={{ min: moment().format("YYYY-MM-DD") }}
                    />
                  )}
                </Box>
              </Box>

              {/* Slots section */}
              {selectedService && (
                <Box className={classes.section}>
                  <Box className={classes.slotsHeader}>
                    <div
                      className={classes.slotsHeaderIcon}
                      style={{ background: brandGradient, color: onPrimary }}
                    >
                      <EventAvailableIcon />
                    </div>
                    <Box>
                      <Typography variant="h6" className={classes.slotsTitle}>
                        Horários Disponíveis
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatLongDate(date)}
                        {!loadingSlots && (
                          <>
                            {" • "}
                            <span
                              className={classes.freeCount}
                              style={{ color: primaryOnLight }}
                            >
                              {`${slots.length} ${
                                slots.length === 1 ? "horário livre" : "horários livres"
                              }`}
                            </span>
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {loadingSlots ? (
                    <Box mt={1}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : slots.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginTop: 8 }}
                    >
                      Nenhum horário livre nesta data.
                    </Typography>
                  ) : (
                    <>
                      {/* Provider pills */}
                      {multipleProviders && (
                        <div className={classes.pillsRow}>
                          <div
                            className={classes.pill}
                            style={
                              providerFilter === "all"
                                ? { background: primary, borderColor: primary, color: onPrimary }
                                : undefined
                            }
                            onClick={() => setProviderFilter("all")}
                          >
                            <GroupIcon fontSize="small" />
                            <span>Todos</span>
                            <span
                              className={classes.pillCount}
                              style={
                                providerFilter === "all"
                                  ? { background: "rgba(255,255,255,0.25)", color: onPrimary }
                                  : undefined
                              }
                            >
                              {slots.length}
                            </span>
                          </div>
                          {groups.map((g) => {
                            const active = String(providerFilter) === String(g.userId);
                            return (
                              <div
                                key={g.userId}
                                className={classes.pill}
                                style={
                                  active
                                    ? { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` }
                                    : undefined
                                }
                                onClick={() => setProviderFilter(g.userId)}
                              >
                                <Avatar
                                  className={classes.pillAvatar}
                                  style={{
                                    backgroundColor: providerColor(g.userId),
                                    color: readableOn(providerColor(g.userId)),
                                  }}
                                >
                                  {initials(g.userName)}
                                </Avatar>
                                <span>{g.userName}</span>
                                <span className={classes.pillCount}>{g.slots.length}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Layout toggle */}
                      {multipleProviders && (
                        <div className={classes.layoutToggleRow}>
                          <div className={classes.layoutToggle}>
                            <button
                              type="button"
                              className={`${classes.layoutBtn} ${
                                layout === "grouped" ? classes.layoutBtnActive : ""
                              }`}
                              onClick={() => setLayout("grouped")}
                            >
                              <ViewAgendaIcon fontSize="small" />
                              Agrupado
                            </button>
                            <button
                              type="button"
                              className={`${classes.layoutBtn} ${
                                layout === "columns" ? classes.layoutBtnActive : ""
                              }`}
                              onClick={() => setLayout("columns")}
                            >
                              <ViewColumnIcon fontSize="small" />
                              Lado a lado
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Provider cards */}
                      {layout === "columns" && multipleProviders ? (
                        <div className={classes.columnsWrap}>
                          {visibleGroups.map((g) => renderProviderCard(g, true))}
                        </div>
                      ) : (
                        visibleGroups.map((g) => renderProviderCard(g, false))
                      )}

                      {/* Legend */}
                      {multipleProviders && (
                        <Box className={classes.legendRow}>
                          {groups.map((g) => (
                            <span key={g.userId} className={classes.legendItem}>
                              <span
                                className={classes.legendSwatch}
                                style={{ background: providerColor(g.userId) }}
                              />
                              <Typography variant="caption" color="textSecondary">
                                {g.userName}
                              </Typography>
                            </span>
                          ))}
                          <span className={classes.legendItem}>
                            <span
                              className={classes.legendSwatch}
                              style={{ background: primary }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              Mais próximo disponível
                            </Typography>
                          </span>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Confirm section */}
              {selectedSlot && (
                <Box
                  className={classes.confirmSection}
                  style={{
                    background: `${primary}0d`,
                    borderColor: `${primary}35`,
                  }}
                >
                  <Box
                    className={classes.confirmHeader}
                    style={{ color: primaryOnLight }}
                  >
                    <CheckCircleIcon fontSize="small" />
                    {`${selectedSlot.userName} · ${moment(selectedSlot.startAt).format(
                      "HH:mm"
                    )} · ${selectedService?.name}`}
                  </Box>
                  <TextField
                    label="Seu nome"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                  />
                  <TextField
                    label="Seu WhatsApp (com DDD)"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    placeholder="5511999999999"
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    style={{ marginTop: 12, backgroundColor: primary, color: onPrimary }}
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? (
                      <CircularProgress size={24} style={{ color: onPrimary }} />
                    ) : (
                      `Confirmar agendamento • ${moment(selectedSlot.startAt).format("HH:mm")}`
                    )}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default PublicBooking;
